import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import moment from "moment";
import {
    ReferenceDot,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartTooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart,
    ReferenceArea,
    Brush,
    Label,
    ReferenceLine,
    Scatter,
    ScatterChart,
    Tooltip
} from 'recharts';
import { selectDateOptions } from "components/Analyses/DatesOptions";
import uuid from "react-uuid";


const toPercentAxis = (decimal) => `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(decimal * 100)}%`;
const toPercent = (decimal, fixed = 0) => `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(decimal * 100)}%`;

const getPercent = (value, total) => {
    const ratio = total > 0 ? value / total : 0;

    return toPercent(ratio, 2);
};

const renderTooltipContent = (o) => {
    const { payload, label } = o;
    const total = payload.reduce((result, entry) => result + entry.value, 0);

    return (
        <div style={{
            backgroundColor: 'rgba(255, 255,255, 0.85)', borderRadius: '4px', padding: '10px', paddingLeft: "10px",
            border: "1px solid #999999"
        }}>
            <p className="total"><b>{label}</b>{` (Total : ${total})`}</p>

            {payload.map((entry, index) => (
                <p key={`item-${index}`} style={{ color: entry.color }}>
                    {`${entry.name}: ${entry.value} (${getPercent(entry.value, total)})`}
                </p>
            ))}

        </div>
    );
};

export const CustomTooltip = ({ active, payload, label, hidelabel }) => {
    //console.log(active, payload, label, xaxistimetick, unit);
    // console.log(chart);
    if (active && payload && payload.length) {
        return (
            <div style={{
                backgroundColor: 'rgba(255, 255,255, 0.85)', borderRadius: '4px', padding: '10px', paddingLeft: "10px",
                border: "1px solid #999999"
            }}>
                {
                    hidelabel ? <></> :
                        <p style={{}}><b>{label}</b></p>
                }

                {
                    payload.map((obj) =>
                        <p style={{ color: obj.color }}>{obj.name + ' : ' + new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(obj.value) + '%'}</p>)
                }

            </div>
        );
    }
    return null;
}

const legends = [{ dataKey: 'actives', hide: false }, { dataKey: 'inactives', hide: false }, { dataKey: 'neverseen', hide: false }]

export default function Columns(props) {
    const { data } = props;
    const [activelegend, setActivelegend] = useState(legends);
    const handleLegendClick = (node) => {
        node.inactive = !node.inactive;
        let activelegendtmp = [...activelegend];
        activelegendtmp.find((legend) => legend.dataKey == node.dataKey).hide = !activelegendtmp.find((legend) => legend.dataKey == node.dataKey).hide;
        setActivelegend(activelegendtmp);
    };
    
    return (

        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                // width={500}
                // height={300}
                stackOffset="expand"
                data={data}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 0,
                }}
            >
                <defs>
                    <radialGradient id="colorRed" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#C53F3F" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#8E0018" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="colorOrange" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#FF763A" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#C54608" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="colorYellow" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#D6D22F" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#A1A100" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="colorGreen" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#91C73C" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#5F9600" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="colorBlue" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#3E8FC6" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#006295" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="colorPurple" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#6B3CC8" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#340C96" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="colorGrey" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#9798A0" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#605F70" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="dpeA" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#008A32" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#476E00" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="dpeB" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#91C73C" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#5F9600" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="dpeC" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#D6D22F" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#A1A100" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="dpeD" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#FFFF66" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#B5B548" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="dpeE" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#F8BB00" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#CE9A00" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="dpeF" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#FF763A" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#C54608" stopOpacity={0.9} />
                    </radialGradient>
                    <radialGradient id="dpeG" x1="1" y1="1" x2="0" y2="0">
                        <stop offset="0%" stopColor="#C53F3F" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#8E0018" stopOpacity={0.9} />
                    </radialGradient>
                </defs>
                <CartesianGrid
                    strokeDasharray="1"
                    vertical={false}
                />
                <XAxis
                    allowDataOverflow={true}
                    dataKey="name"
                    // angle={325}
                    tickMargin={5}
                    axisLine={false}
                    // tick={displaytick}
                    domain={["auto", "auto"]}
                />
                <YAxis
                    // label={{ value: '%', angle: -90, position: 'insideLeft', offset: 10 }}
                    yAxisId="left"
                    axisLine={false}
                    tickLine={false}
                    // domain={[0, 100]}
                    tickFormatter={toPercentAxis}
                />
                <Tooltip
                    cursor={{ fill: 'transparent' }}
                    offset={0}
                    content={renderTooltipContent}
                />
                <Legend
                    wrapperStyle={{ marginBottom: 0 }}
                    onClick={handleLegendClick}
                />
                <Bar yAxisId="left" name="Actifs" dataKey="actives" stackId="a" fill="url(#colorGreen)" hide={activelegend.find((legend) => legend.dataKey == "actives").hide} />
                <Bar yAxisId="left" name="Inactifs" dataKey="inactives" stackId="a" fill="url(#colorRed)" hide={activelegend.find((legend) => legend.dataKey == "inactives").hide} />
                <Bar yAxisId="left" name="Jamais vus" dataKey="neverseen" stackId="a" fill="url(#colorGrey)" hide={activelegend.find((legend) => legend.dataKey == "neverseen").hide} />

            </BarChart>

        </ResponsiveContainer>

    );
}